import React, { useState, useEffect, useCallback } from 'react';
import { accountingAPI, projectsAPI, contactsAPI } from '../../../services/api';
import { StatCard }    from '../../../components/common/StatCard';
import { PageHeader }  from '../../../components/layout/PageHeader';
import {
  CurrencyRupeeIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon,
  ScaleIcon, UserGroupIcon, FolderIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const COLORS  = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];

function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const fmt = n => '₹' + Number(n||0).toLocaleString('en-IN');
const fmtK = n => { const v=Number(n||0); return v>=100000?'₹'+(v/100000).toFixed(1)+'L':v>=1000?'₹'+(v/1000).toFixed(0)+'K':'₹'+v; };

export function AnalyticsDashboard() {
  const [range,    setRange]    = useState('thisYear');
  const [summary,  setSummary]  = useState(null);
  const [pl,       setPL]       = useState(null);
  const [contacts, setContacts] = useState({ customers:0, vendors:0 });
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const getDates = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (range === 'thisMonth') return { startDate: new Date(y,m,1).toISOString().split('T')[0], endDate: new Date(y,m+1,0).toISOString().split('T')[0] };
    if (range === 'lastMonth') return { startDate: new Date(y,m-1,1).toISOString().split('T')[0], endDate: new Date(y,m,0).toISOString().split('T')[0] };
    if (range === 'thisYear')  return { startDate: new Date(y,0,1).toISOString().split('T')[0],   endDate: new Date(y,11,31).toISOString().split('T')[0] };
    if (range === 'lastYear')  return { startDate: new Date(y-1,0,1).toISOString().split('T')[0], endDate: new Date(y-1,11,31).toISOString().split('T')[0] };
    return { startDate: new Date(y,0,1).toISOString().split('T')[0], endDate: new Date(y,11,31).toISOString().split('T')[0] };
  }, [range]);

  const load = useCallback(async () => {
    setLoading(true);
    const dates = getDates();
    try {
      const [sumRes, plRes, custRes, vendRes, projRes] = await Promise.allSettled([
        accountingAPI.getSummary(dates),
        accountingAPI.getProfitLoss(dates),
        contactsAPI.getAll({ type:'customer', limit:1 }),
        contactsAPI.getAll({ type:'vendor',   limit:1 }),
        projectsAPI.getStats(),
      ]);
      if (sumRes.status==='fulfilled') setSummary(sumRes.value.data);
      if (plRes.status ==='fulfilled') setPL(plRes.value.data);
      setContacts({
        customers: custRes.status==='fulfilled' ? (custRes.value.pagination?.total||0) : 0,
        vendors:   vendRes.status==='fulfilled' ? (vendRes.value.pagination?.total||0) : 0,
      });
      if (projRes.status==='fulfilled') setProjects(projRes.value.data||[]);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [getDates]);

  useEffect(() => { load(); }, [load]);

  // Build monthly revenue/expense chart data
  const monthlyMap = {};
  (pl?.monthly || []).forEach(m => {
    const key = `${m._id.year}-${String(m._id.month).padStart(2,'0')}`;
    if (!monthlyMap[key]) monthlyMap[key] = { month: MONTHS[m._id.month-1]+' '+String(m._id.year).slice(2), revenue:0, expense:0, profit:0 };
    if (['sale','indirect_income'].includes(m._id.type)) monthlyMap[key].revenue += m.total;
    if (['purchase','expense'].includes(m._id.type))     monthlyMap[key].expense += m.total;
  });
  const monthlyData = Object.values(monthlyMap).map(m => ({ ...m, profit: m.revenue - m.expense }));

  // Expense breakdown for pie
  const expPie = (pl?.expenseByCategory||[]).map(e => ({ name: e._id, value: e.total }));

  // Summary quick stats
  const revenue   = summary?.revenue   || 0;
  const costs     = summary?.costs     || 0;
  const netProfit = summary?.netProfit || 0;
  const margin    = revenue > 0 ? ((netProfit/revenue)*100).toFixed(1) : 0;

  const RANGES = [
    { key:'thisMonth', label:'This Month' },
    { key:'lastMonth', label:'Last Month' },
    { key:'thisYear',  label:'This Year'  },
    { key:'lastYear',  label:'Last Year'  },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Analytics Dashboard" subtitle="Real-time business performance insights" />

      {/* Range picker */}
      <div className="flex gap-2 flex-wrap">
        {RANGES.map(r => (
          <button key={r.key} onClick={() => setRange(r.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${range===r.key ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Revenue"  value={loading?'...':fmtK(revenue)}   icon={ArrowTrendingUpIcon}   color="green"
          subtitle={loading?'':fmt(revenue)} />
        <StatCard title="Total Costs"    value={loading?'...':fmtK(costs)}     icon={ArrowTrendingDownIcon} color="red"
          subtitle={loading?'':fmt(costs)} />
        <StatCard title="Net Profit"     value={loading?'...':fmtK(netProfit)} icon={ScaleIcon}             color={netProfit>=0?'indigo':'red'}
          subtitle={loading?'':margin+'% margin'} />
        <StatCard title="Customers"      value={loading?'...':contacts.customers} icon={UserGroupIcon}       color="blue"
          subtitle={`${contacts.vendors} vendors`} />
      </div>

      {/* Revenue vs Expense trend */}
      <Card title="Revenue vs Costs — Monthly Trend">
        {loading
          ? <div className="h-60 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
          : monthlyData.length === 0
            ? <div className="h-60 flex items-center justify-center text-gray-400 text-sm">No data for selected period</div>
            : <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.15}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tickFormatter={v=>'₹'+v/1000+'K'} tick={{fontSize:11}} />
                  <Tooltip formatter={v=>fmt(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#6366f1" fill="url(#gRev)" strokeWidth={2}/>
                  <Area type="monotone" dataKey="expense" name="Costs"   stroke="#ef4444" fill="url(#gExp)" strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
        }
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Net Profit bar */}
        <Card title="Monthly Net Profit">
          {loading
            ? <div className="h-52 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
            : <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                  <XAxis dataKey="month" tick={{fontSize:10}}/>
                  <YAxis tickFormatter={v=>'₹'+v/1000+'K'} tick={{fontSize:10}}/>
                  <Tooltip formatter={v=>fmt(v)}/>
                  <Bar dataKey="profit" name="Net Profit" radius={[4,4,0,0]}>
                    {monthlyData.map((m,i) => (
                      <Cell key={i} fill={m.profit>=0 ? '#6366f1' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
          }
        </Card>

        {/* Expense breakdown pie */}
        <Card title="Expense Breakdown by Category">
          {loading || expPie.length===0
            ? <div className="h-52 flex items-center justify-center text-gray-400 text-sm">{loading?'Loading...':'No expense data'}</div>
            : <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={expPie} cx="45%" cy="50%" outerRadius={80} dataKey="value"
                    label={({name,percent})=>name+' '+Math.round(percent*100)+'%'}
                    labelLine={false} fontSize={10}>
                    {expPie.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={v=>fmt(v)}/>
                </PieChart>
              </ResponsiveContainer>
          }
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Top customers */}
        <Card title="Top 10 Customers by Revenue">
          {loading
            ? <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
            : (pl?.topCustomers||[]).length === 0
              ? <div className="text-center py-10 text-gray-400 text-sm">No sales data for period</div>
              : <div className="space-y-2.5">
                  {(pl?.topCustomers||[]).map((c,i) => {
                    const pct = pl.topCustomers[0]?.revenue > 0 ? (c.revenue/pl.topCustomers[0].revenue)*100 : 0;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-4 text-right">{i+1}</span>
                        <div className="flex-1">
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="font-medium text-gray-700 truncate max-w-36">{c.name||'Unknown'}</span>
                            <span className="text-indigo-600 font-semibold ml-2 flex-shrink-0">{fmtK(c.revenue)}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full transition-all" style={{width:pct+'%'}}/>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 w-12 text-right">{c.count} inv</span>
                      </div>
                    );
                  })}
                </div>
          }
        </Card>

        {/* Projects breakdown */}
        <Card title="Projects by Status">
          {loading || projects.length===0
            ? <div className="h-40 flex items-center justify-center text-gray-400 text-sm">{loading?'Loading...':'No project data'}</div>
            : <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={projects.map(p=>({name:p._id,value:p.count}))}
                      cx="50%" cy="50%" outerRadius={65} dataKey="value"
                      label={({name,value})=>name+' ('+value+')'}
                      labelLine={false} fontSize={10}>
                      {projects.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                    </Pie>
                    <Tooltip/>
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2">
                  {projects.map((p,i) => (
                    <span key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{background:COLORS[i%COLORS.length]}}/>
                      {p._id}: <strong>{p.count}</strong>
                    </span>
                  ))}
                </div>
              </>
          }
        </Card>
      </div>
    </div>
  );
}